<template>
  <div>
    <h2>{{ t('listerBranding.title') }}</h2>
    <div :class="[$style.logoHolder, { [$style.hasLogo]: listerBranding.logoUrl }]">
      <img
        v-if="listerBranding.logoUrl && !shouldShowAgencyProfilePage"
        :class="$style.logo"
        :src="listerBranding.logoUrl"
        :alt="listerBranding.name || listerBranding.legalName || ''"
      />
      <a
        v-if="listerBranding.logoUrl && shouldShowAgencyProfilePage"
        :href="agencyProfileLink"
        :title="
          t('listerBranding.agencyProfileLinkDynamicText', {
            agencyDynamicName: getAgencyFullName,
          })
        "
        @click="onAgencyProfileLinkClicked(InteractionContainerName.LOGO)"
      >
        <img
          :class="$style.logo"
          :src="listerBranding.logoUrl"
          :alt="listerBranding.name || listerBranding.legalName || ''"
        />
      </a>
      <PartnerBadge v-if="partnerBadge" :badge="partnerBadge" :class="$style.partnerBadge" />
    </div>

    <address>
      <div v-if="listerBranding.legalName">
        {{ listerBranding.legalName }}
      </div>
      <div v-if="listerBranding.name">
        {{ listerBranding.name }}
      </div>
      <div v-if="listerBranding.address">
        <div v-if="listerBranding.address.street">
          {{ listerBranding.address.street }}
        </div>
        <div v-if="listerBranding.address.streetAddition">
          {{ listerBranding.address.streetAddition }}
        </div>
        <div>
          <span v-if="listerBranding.address.postalCode" :class="$style.postalCode">
            {{ listerBranding.address.postalCode }} </span
          ><span v-if="listerBranding.address.locality">{{ listerBranding.address.locality }}</span>
        </div>
      </div>
    </address>
    <IndustryAssociationBadges v-if="agencyAssociations" :badges="agencyAssociations" />
    <div v-if="shouldShowAgencyProfilePage">
      <a
        :href="agencyProfileLink"
        @click="onAgencyProfileLinkClicked(InteractionContainerName.LINK)"
        >{{
          t('listerBranding.agencyProfileLinkDynamicText', {
            agencyDynamicName: getAgencyFullName,
          })
        }}</a
      >
    </div>
  </div>
</template>

<script lang="ts" setup>
import { HgSupportedLanguage, ListerBranding } from '@smg-real-estate/hg-search-libs';
import { computed, ref } from 'vue';

import IndustryAssociationBadges from '@/components/IndustryAssociationBadges/IndustryAssociationBadges.vue';
import type { AssociationBadges } from '@/components/IndustryAssociationBadges/types';
import PartnerBadge from '@/components/PartnerBadge/PartnerBadge.vue';
import { InteractionContainerName } from '@/helpers/GTM/GTMParameters';
import type { PartnerBadge as PartnerBadgeData } from '@/helpers/partnerBadge/getPartnerBadge';
import { createAgencyProfilePageRoute } from '@/views/AgencyProfilePage/agencyProfilePageRoutingHelpers';
import { trackAgencyProfileLinkClicked } from '@/views/AgencyProfilePage/agencyProfileTracking';
import { useI18n } from 'vue-i18n';

const { t, locale } = useI18n();
defineOptions({
  name: 'ListerBranding',
});

interface Props {
  listerBranding: ListerBranding;
  listerId?: string;
  partnerBadge?: PartnerBadgeData | null;
}
const props = withDefaults(defineProps<Props>(), {
  listerId: '',
  partnerBadge: null,
});

const agencyAssociations = ref<AssociationBadges | null>(null);

const language = computed<HgSupportedLanguage>(() => {
  return locale.value as HgSupportedLanguage;
});
const agencyProfileLink = computed<string | undefined>(() => {
  const urlKeyword = props.listerBranding.profilePageUrlKeyword;
  return urlKeyword && props.listerId
    ? createAgencyProfilePageRoute(language.value, urlKeyword, props.listerId)
    : undefined;
});
const shouldShowAgencyProfilePage = computed<boolean>(() => {
  return !!agencyProfileLink.value;
});
const getAgencyFullName = computed<string>(() => {
  return `${props.listerBranding.name || props.listerBranding.legalName}`;
});

function onAgencyProfileLinkClicked(
  containerName: InteractionContainerName.LINK | InteractionContainerName.LOGO,
): void {
  if (agencyProfileLink.value) {
    trackAgencyProfileLinkClicked(containerName, agencyProfileLink.value);
  }
}
</script>

<style module lang="scss">
@import '@/assets/variables';

$lister-logo-max-width: 120px;
$lister-logo-max-height: 60px;
$lister-partner-badge-size: 8rem;
$lister-branding-margin-bottom: $space-75;

address {
  padding: 0;
  margin: 0;
  font-style: normal;
  line-height: var(--typo-heading-2-line-height);

  > div {
    hyphens: auto;
    word-break: break-word;
    overflow-wrap: break-word;
  }
}

.logoHolder {
  display: flex;
  align-items: center;
  margin-bottom: $lister-branding-margin-bottom;
}

.logoHolder.hasLogo .partnerBadge {
  margin-left: auto;
}

.logo {
  display: block;
  width: 100%;
  max-width: $lister-logo-max-width;
  height: auto;
  max-height: $lister-logo-max-height;
  margin-right: $space-75;
  object-fit: contain;
}

.logoHolder > a {
  display: flex;
  align-items: center;
}

.partnerBadge {
  width: $lister-partner-badge-size;
  height: $lister-partner-badge-size;
}

.postalCode {
  margin-right: $space-25;
}
</style>
